import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import '../LandingPage.css';
import AuthModal from './auth/AuthModal';
import { createCheckoutSession } from '../services/documentApi';
import { signOutUser } from '../utils/auth';
import AvatarMenu from './shared/AvatarMenu';
import SiteFooter from './shared/SiteFooter';

/* ─── SVG icons ─── */
const IconResume = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);
const IconEnvelope = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const IconPerson = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconShare = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const IconGrid = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);
const IconTarget = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);
const IconSearch = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IconBolt = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconSend = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconGlobe = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconCard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

/* ─── Static data ─── */
const DOCS = [
  { icon: <IconResume />,   name: 'Resume or CV',      desc: 'ATS-optimized resume or full academic CV, your choice' },
  { icon: <IconEnvelope />, name: 'Cover Letter',      desc: 'Personalized to the company' },
  { icon: <IconPerson />,   name: 'Professional Bio',  desc: 'Third-person, role-appropriate' },
  { icon: <IconShare />,    name: 'LinkedIn Profile',  desc: 'Headline, about, and experience' },
  { icon: <IconGrid />,     name: 'Portfolio',         desc: 'Work narrative and highlights' },
];

const FEATURES = [
  { icon: <IconTarget />, title: 'Role-specific rewriting', desc: 'Not a template fill-in. Every sentence rewritten to match the language and requirements of the job posting.' },
  { icon: <IconSearch />, title: 'ATS keyword matching',    desc: 'Structured formatting and precision keyword placement built to pass automated screening before a human sees your application.' },
  { icon: <IconBolt />,   title: 'Instant generation',      desc: 'Your complete application package from one session. No waiting, no back-and-forth, no editing required.' },
  { icon: <IconSend />,   title: 'Ready-to-send files',     desc: 'Download polished .docx files formatted to professional standards. Open, review, send.' },
  { icon: <IconGlobe />,  title: 'Any role, any industry',  desc: 'Tech, finance, healthcare, marketing, legal. Zynacular adapts to the industry and seniority level of the role.' },
  { icon: <IconCard />,   title: 'Pay per use',             desc: 'No subscription. No monthly fee. Pay only for what you need. One session covers your entire application package.' },
];

const PRICING_TIERS = [
  { badge: null,           sku: 'EMP-APP-1',    name: 'Starter',    credits: '1 credit',   price: '$3.99',  per: '$3.99 / session', desc: 'Perfect for one application.',                        popular: false },
  { badge: null,           sku: 'EMP-APP-3',    name: 'Value Pack', credits: '3 credits',  price: '$9.99',  per: '$3.33 / session', desc: 'Save 17% per application. Great for applying to a few roles.', popular: false },
  { badge: 'MOST POPULAR', sku: 'EMP-APP-10',   name: 'Pro Pack',   credits: '10 credits', price: '$29.99', per: '$3.00 / session', desc: 'Best value for active job seekers. Save 25%.',        popular: true  },
  { badge: 'ADD-ON',       sku: 'EMP-ADDON-10', name: 'Top-Up',     credits: '10 credits', price: '$24.99', per: '$2.50 / session', desc: 'Existing customers only. Lowest cost per credit.',    popular: false },
];

const FAQS = [
  {
    q: 'What exactly is a credit?',
    a: 'One credit covers one complete application package for one job. Upload your resume, paste the job description, and Zynacular generates a tailored resume, cover letter, and supporting documents.',
  },
  {
    q: 'Do my credits expire?',
    a: 'No. Credits never expire. Buy them when you need them, use them at your own pace.',
  },
  {
    q: 'Will it pass ATS filters?',
    a: 'Zynacular is designed using ATS best practices, including clean formatting and role-specific keyword optimization, to maximize compatibility with modern applicant tracking systems.',
  },
  {
    q: 'What file format do I get?',
    a: 'All documents are delivered as .docx files, formatted to professional standards and ready to send.',
  },
  {
    q: 'Is there a subscription?',
    a: 'No. Zynacular is strictly pay-per-use. You buy credits, you use them. No recurring charges, no monthly fees, no surprises.',
  },
];

/* ─── Page ─── */
const LandingPage = () => {
  const session = useSelector((state) => state.auth.session);
  const user = useSelector((state) => state.auth.user);
  const credits = useSelector((state) => state.auth.credits);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const firstMenuLinkRef = useRef(null);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (menuOpen) {
      firstMenuLinkRef.current?.focus();
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 720) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOut = () => signOutUser({ dispatch, navigate, userId: user?.id });
  const [loadingSku, setLoadingSku] = useState(null);

  const appDest = credits === 0 ? '/get-credits' : '/create';

  useEffect(() => {
    if (session && showModal) {
      setShowModal(false);
      // If AuthModal is handling a pendingSku checkout redirect, don't race it with a navigate
      if (!sessionStorage.getItem('pendingSku')) {
        navigate(appDest);
      }
    }
  }, [session, showModal, navigate, appDest]);

  useEffect(() => {
    if (pathname === '/pricing') {
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 50);
    }
  }, [pathname]);

  const handleCta = () => {
    if (session) {
      navigate(appDest);
    } else {
      setShowModal(true);
    }
  };

  const handlePricingCta = async (sku) => {
    if (session) {
      setLoadingSku(sku);
      try {
        const { url } = await createCheckoutSession(sku, session.access_token);
        window.location.href = url;
      } catch (err) {
        console.error('[Checkout]', err.message);
        setLoadingSku(null);
      }
    } else {
      sessionStorage.setItem('pendingSku', sku);
      setShowModal(true);
    }
  };

  return (
    <div className="lp-root">
      {showModal && <AuthModal onClose={() => setShowModal(false)} />}
      <div className="lp-ambient" aria-hidden="true" />

      {/* ── NAV ── */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <span className="lp-nav-logomark">Z</span>
          <span className="lp-nav-logo">ZYNACULAR</span>
        </div>
        <div className="lp-nav-links">
          <a href="#how-it-works" className="lp-nav-link">How it works</a>
          <a href="#what-you-get" className="lp-nav-link">Features</a>
          <a href="#pricing" className="lp-nav-link">Pricing</a>
          <Link to="/story" className="lp-nav-link">Our Story</Link>
          <Link to="/support" className="lp-nav-link">Support</Link>
          <span className="lp-nav-sep" aria-hidden="true" />
          {session ? (
            <>
              <AvatarMenu
                userEmail={user?.email}
                credits={credits}
                onSignOut={handleSignOut}
              />
              <button onClick={handleCta} className="lp-nav-cta">Go to App →</button>
            </>
          ) : (
            <>
              <button onClick={handleCta} className="lp-nav-signin">Sign in</button>
              <button onClick={handleCta} className="lp-nav-cta">Launch App →</button>
            </>
          )}
          <button
            type="button"
            ref={hamburgerRef}
            className="lp-nav-hamburger"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="lp-mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="lp-hamburger-bar" />
            <span className="lp-hamburger-bar" />
            <span className="lp-hamburger-bar" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <>
          <div className="lp-mobile-menu-overlay" onClick={closeMenu} aria-hidden="true" />
          <nav id="lp-mobile-menu" className="lp-mobile-menu" aria-label="Mobile">
            <a
              ref={firstMenuLinkRef}
              href="#how-it-works"
              className="lp-mobile-menu-link"
              onClick={closeMenu}
            >
              How it works
            </a>
            <a href="#what-you-get" className="lp-mobile-menu-link" onClick={closeMenu}>Features</a>
            <a href="#pricing" className="lp-mobile-menu-link" onClick={closeMenu}>Pricing</a>
            <Link to="/story" className="lp-mobile-menu-link" onClick={closeMenu}>Our Story</Link>
            <Link to="/support" className="lp-mobile-menu-link" onClick={closeMenu}>Support</Link>
          </nav>
        </>
      )}

      {/* ── HERO ── */}
      <section className="lp-hero">
        <span className="lp-hero-eyebrow">AI-POWERED · PAY PER USE · NO SUBSCRIPTION</span>
        <h1 className="lp-hero-headline">
          Your career,<br />
          rewritten by <span className="lp-accent">AI.</span>
        </h1>
        <p className="lp-hero-sub">
          Paste your resume, add a job description, and get a fully tailored application package,
          ATS-optimized and ready to send.
        </p>
        <div className="lp-hero-buttons">
          <button onClick={handleCta} className="lp-cta">Get Started →</button>
          <a href="#how-it-works" className="lp-cta-ghost">See how it works</a>
        </div>
        <div className="lp-proof-strip">
          <div className="lp-proof-item">
            <span className="lp-proof-num">1 session</span>
            <span className="lp-proof-label">Full package</span>
          </div>
          <span className="lp-proof-dot" aria-hidden="true" />
          <div className="lp-proof-item">
            <span className="lp-proof-num">No subscription</span>
            <span className="lp-proof-label">Pay per use</span>
          </div>
          <span className="lp-proof-dot" aria-hidden="true" />
          <div className="lp-proof-item">
            <span className="lp-proof-num">ATS-ready</span>
            <span className="lp-proof-label">Optimized output</span>
          </div>
          <span className="lp-proof-dot" aria-hidden="true" />
          <div className="lp-proof-item">
            <span className="lp-proof-num">$3.99</span>
            <span className="lp-proof-label">Starts from</span>
          </div>
        </div>

        {/* ── PRODUCT PREVIEW ── */}
        <div className="lp-hero-preview" aria-hidden="true">
          <div className="lp-preview-doc lp-preview-doc--left">
            <div className="lp-preview-doc-label">Cover Letter</div>
            <div className="lp-preview-lines">
              <div className="lp-pl" style={{width:'78%'}} />
              <div className="lp-pl" style={{width:'92%'}} />
              <div className="lp-pl" style={{width:'60%'}} />
              <div className="lp-pl" style={{width:'84%'}} />
              <div className="lp-pl" style={{width:'50%'}} />
            </div>
          </div>
          <div className="lp-preview-doc lp-preview-doc--center">
            <div className="lp-preview-doc-label">Resume</div>
            <div className="lp-pl" style={{width:'62%', height:'9px', marginBottom:'5px'}} />
            <div className="lp-pl" style={{width:'44%', height:'6px'}} />
            <div className="lp-preview-divider" />
            <div className="lp-preview-section">Experience</div>
            <div className="lp-pl" style={{width:'84%'}} />
            <div className="lp-pl" style={{width:'72%'}} />
            <div className="lp-pl" style={{width:'90%'}} />
            <div className="lp-preview-section" style={{marginTop:'12px'}}>Skills</div>
            <div className="lp-preview-chips">
              <span className="lp-preview-chip">Leadership</span>
              <span className="lp-preview-chip">Strategy</span>
              <span className="lp-preview-chip">Communication</span>
            </div>
            <div className="lp-preview-watermark">ZYNACULAR</div>
          </div>
          <div className="lp-preview-doc lp-preview-doc--right">
            <div className="lp-preview-doc-label">LinkedIn</div>
            <div className="lp-preview-lines">
              <div className="lp-pl" style={{width:'86%'}} />
              <div className="lp-pl" style={{width:'62%'}} />
              <div className="lp-pl" style={{width:'94%'}} />
              <div className="lp-pl" style={{width:'70%'}} />
              <div className="lp-pl" style={{width:'55%'}} />
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="lp-how-section">
        <p className="lp-section-label">HOW IT WORKS</p>
        <h2 className="lp-section-heading">Three steps to your complete application</h2>
        <p className="lp-section-sub">No templates. No generic output. Every document rewritten from your actual resume for the specific role.</p>
        <div className="lp-steps-card">
          <div className="lp-step">
            <div className="lp-step-num">1</div>
            <div className="lp-step-title">Paste your resume</div>
            <p className="lp-step-desc">Upload your existing resume or paste it in. Zynacular reads your experience, skills, and background.</p>
          </div>
          <div className="lp-step-divider" aria-hidden="true" />
          <div className="lp-step">
            <div className="lp-step-num">2</div>
            <div className="lp-step-title">Add the job description</div>
            <p className="lp-step-desc">Drop in the job posting. Our AI extracts the role, company, requirements, and keywords automatically.</p>
          </div>
          <div className="lp-step-divider" aria-hidden="true" />
          <div className="lp-step">
            <div className="lp-step-num">3</div>
            <div className="lp-step-title">Download everything</div>
            <p className="lp-step-desc">Get your complete application package: formatted, ATS-ready, and ready to send.</p>
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GET + FEATURES (COMBINED) ── */}
      <section id="what-you-get" className="lp-wide-section">
        <p className="lp-section-label">WHAT YOU GET</p>
        <h2 className="lp-section-heading">Your complete package. One generation.</h2>
        <p className="lp-section-sub">Everything you need for a complete application, tailored to the specific role and company.</p>
        <div className="lp-docs-grid-full">
          {DOCS.map((doc) => (
            <div key={doc.name} className="lp-doc-item">
              <div className="lp-feature-icon">{doc.icon}</div>
              <div>
                <div className="lp-feature-title">{doc.name}</div>
                <p className="lp-feature-desc">{doc.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="lp-differentiators">
          {[FEATURES[0], FEATURES[2], FEATURES[5]].map((f, i, arr) => (
            <React.Fragment key={f.title}>
              <div className="lp-differentiator">
                <div className="lp-feature-icon">{f.icon}</div>
                <div className="lp-differentiator-label">{f.title}</div>
                <p className="lp-differentiator-desc">{f.desc.split('. ')[0]}.</p>
              </div>
              {i < arr.length - 1 && <div className="lp-differentiator-sep" aria-hidden="true" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="lp-wide-section">
        <p className="lp-section-label">PRICING</p>
        <h2 className="lp-section-heading">Simple, transparent pricing</h2>
        <p className="lp-section-sub">One credit = one complete application package tailored to one job. No subscriptions. No recurring fees. Just pay for what you need.</p>
        <p className="lp-pricing-value-note">Career coaches charge $300–$500 for a single resume rewrite. <strong>One Zynacular session starts at $3.99.</strong></p>
        <div className="lp-pricing-grid">
          {PRICING_TIERS.map((tier) => (
            <div key={tier.name} className={`lp-pricing-card${tier.popular ? ' lp-pricing-card--popular' : ''}`}>
              {tier.badge && (
                <span className={`lp-pricing-badge${tier.popular ? ' lp-pricing-badge--popular' : ''}`}>
                  {tier.badge}
                </span>
              )}
              <div className="lp-pricing-name">{tier.name}</div>
              <div className="lp-pricing-credits">{tier.credits}</div>
              <div className="lp-pricing-price">{tier.price}</div>
              <div className="lp-pricing-per">{tier.per}</div>
              <p className="lp-pricing-desc">{tier.desc}</p>
              <button
                onClick={() => handlePricingCta(tier.sku)}
                disabled={loadingSku !== null}
                className={`lp-pricing-cta${tier.popular ? ' lp-pricing-cta--filled' : tier.sku === 'EMP-ADDON-10' ? ' lp-pricing-cta--teal' : ''}`}
                style={loadingSku !== null ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
              >
                {loadingSku === tier.sku ? 'Loading…' : 'Get Started →'}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="lp-how-section">
        <p className="lp-section-label">FAQ</p>
        <h2 className="lp-section-heading">Common questions</h2>
        <div className="lp-faq-list">
          {FAQS.map((item, i) => (
            <div key={i} className="lp-faq-item">
              <div className="lp-faq-q">{item.q}</div>
              <div className="lp-faq-a">{item.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="lp-final-cta">
        <div className="lp-final-cta-glow" aria-hidden="true" />
        <h2 className="lp-final-cta-heading">Ready to land more interviews?</h2>
        <p className="lp-final-cta-sub">Your full application set, ready in one session.</p>
        <button
          onClick={() => handlePricingCta('EMP-APP-1')}
          disabled={loadingSku !== null}
          className="lp-cta"
          style={loadingSku !== null ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
        >
          {loadingSku === 'EMP-APP-1' ? 'Loading…' : 'Get Started for $3.99 →'}
        </button>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
